<template>
  <div class="tab-content">
    <!-- 顶部操作区域 -->
    <div class="table-header">
      <span></span>
      <Button type="primary" @click="showAddModal">新增文书</Button>
    </div>
    <div class="table-container">
      <a-table
        :columns="columns"
        :data-source="documentList"
        :pagination="pagination"
        row-key="id"
        size="middle"
        :scroll="{ x: 1200 }"
      >
        <template #bodyCell="{ column, record }">
          <template v-if="column.key === 'fileName'">
            <div>
              <a style="color: #1e8ef2" :href="record.fileName" v-if="record.fileName"
                 target="_blank">{{ getFileName(record.fileName) }}</a>
            </div>
          </template>
          <template v-if="column.key === 'status'">
            <span :style="{
              color: record.status === 1 ? '#52c41a' : record.status === 0 ? '#1890ff' : '#bfbfbf'
            }">
              {{ record.status === 1 ? '已签收' : record.status === 0 ? '待签收' : '-' }}
            </span>
          </template>
          <template v-if="column.key === 'action'">
            <span>
              <a-button class="action-btn" type="link" @click="handleFilePreview(record)">{{ record.status === 1 ? '预览' : record.status === 0 ? '签收' : '-' }}</a-button>
              <a-button class="action-btn" type="link" @click="downloadFile(record)">下载</a-button>
              <a-button class="action-btn" type="link" danger @click="deleteFile(record)">删除</a-button>
            </span>
          </template>
        </template>
      </a-table>
    </div>
    
    <!-- 添加文书模态框 -->
    <Modal
      title="添加通知文书"
      v-model:visible="addModalVisible"
      @ok="submitAddDocument"
      @cancel="addModalVisible = false"
      okText="确认"
      cancelText="取消"
      width="900px"
    >
      <Form layout="vertical">
        <Form.Item label="文书名称">
          <Input v-model:value="addDocumentForm.documentName" placeholder="请输入文书名称" />
        </Form.Item>
        <Form.Item label="上传文件">
          <fileUpload
              class="pl64"
              :updateTitle="'点击上传'"
              ref="uploadRef"
              accept=".doc,.docx,.pdf,.jpg,.png,.xls,.xlsx"
              :maxCount="1"
              pathName="application"
              @fileListChange="onFileListChange">
          </fileUpload>
          <p class="text-xs text-gray-500 mt-1">支持上传Word、PDF等格式文件，大小不超过10MB</p>
        </Form.Item>
      </Form>
    </Modal>
    <FileReceive
        v-model:visible="previewVisible"
        :document="currentDocument"
        @close="handlePreviewClose"
        @receive-success="handleReceiveSuccess"
    />
  </div>
</template>

<script setup name="NoticeOfficeTab">
import { ref, reactive, onMounted, defineProps, defineEmits } from 'vue';
import { Button, Modal, Form, Input, message, Upload } from 'ant-design-vue';
import fileUpload from '@/components/fileUpload';
import FileReceive from '@/components/FileReceive';
import {
  getDocumentListByApplicationId,
  getDocumentUnreadCount,
  deleteDocumentById,
  saveDocument
} from '@/api/application/documents'
import FilePreview from "@/components/FilePreview/index.vue";

// 定义组件接收的props
const props = defineProps({
  id: {
    type: String,
    required: true
  }
});

// 定义事件
const emit = defineEmits(['upload-application-info', 'update-unread-count']);

// 通知文书列表数据
const documentList = ref([]);

// 分页相关状态变量
const pagination = reactive({
  total: 0,
  size: "middle",
  defaultPageSize: 10,
  current: 1,
  pageSize: 10,
  showSizeChanger: true,
  showQuickJumper: false,
  pageSizeOptions: ["10", "20", "30"],
  showTotal: (total) => `共有 ${total} 条数据`,
  onShowSizeChange: (current, pageSize) => (pagination.pageSize = pageSize),
  onChange: pageChange,
  showSizeChange: pageSizeChange,
});

function pageChange(page, pageSize) {
  pagination.current = page;
  fetchDocumentData();
}

function pageSizeChange(current, size) {
  pagination.pageSize = size;
  fetchDocumentData();
}

// 模态框状态
const addModalVisible = ref(false);
const currentDocument = ref(null);

// 表格列定义
const columns = [
  {
    title: '文书名称',
    dataIndex: 'documentName',
    key: 'documentName',
    width: 300
  },
  {
    title: '文件名称',
    dataIndex: 'fileName',
    key: 'fileName',
    width: 300,
    ellipsis: true,
    tooltip: (text) => text
  },
  {
    title: '状态',
    dataIndex: 'status',
    key: 'status',
    width: 120
  },
  {
    title: '上传时间',
    dataIndex: 'createTime',
    key: 'createTime',
    width: 200
  },
  {
    title: '操作',
    key: 'action',
    width: 150
  }
];

// 添加文书表单
const addDocumentForm = reactive({
  documentName: ''
});

// 上传文件列表
const fileList = ref([]);

// 处理文件上传变化
const onFileListChange = (newFileList) => {
  // 只存储状态为done的文件的URL地址
  fileList.value = newFileList
      .filter(file => file.status === 'done')
      .map(file => {
        // 根据不同情况获取URL
        if (file.url) return file.url;
        return '';
      });
};

// 打开添加文书模态框
const showAddModal = () => {
  resetForm();
  addModalVisible.value = true;
};

// 重置表单
const resetForm = () => {
  addDocumentForm.documentName = '';
  fileList.value = [];
};

// 提交添加文书
const submitAddDocument = () => {
  // 表单验证
  if (!addDocumentForm.documentName || fileList.value.length === 0) {
    message.error('请填写文书名称并上传文件');
    return;
  }

  // 添加新文书
  const newDocument = {
    applicationId: props.id,
    documentName: addDocumentForm.documentName,
    fileName: getFileName(fileList.value[0]),
    fileUrl: fileList.value[0],
  };

  saveDocument(newDocument).then(res => {
    if (res.code === 200){
      addModalVisible.value = false;
      message.success('文书添加成功');
      fetchDocumentData();
      // 通知父组件更新未读通知数量
      emit('update-unread-count');
    }
  })
  resetForm();
};

// 预览相关状态
const previewVisible = ref(false);
// 处理文件点击预览
const handleFilePreview = (file) => {
  handlePreview(file);
};

// 处理文件预览
const handlePreview = (document) => {
  if (document && document.fileUrl) {
    currentDocument.value = document;
    previewVisible.value = true;
  }
};

// 关闭预览
const handlePreviewClose = () => {
  previewVisible.value = false;
};

// 下载文件
const downloadFile = (record) => {
  if (!record.fileName) return;

  // 实际应用中应该调用下载API
  window.location.href = `/api/download?file=${record.fileName}`;
};

// 删除文件
const deleteFile = (record) => {
  Modal.confirm({
    title: '确定要删除该文书吗？',
    content: '删除后无法恢复',
    okText: '确定',
    cancelText: '取消',
    onOk: () => {
      deleteDocumentById(record.id).then(res =>{
        if (res.code === 200){
          message.success('文书删除成功');
          fetchDocumentData();
          // 通知父组件更新未读通知数量
          emit('update-unread-count');
        }
      })
    }
  });
};

// 获取数据
const fetchDocumentData = async () => {
  try {
    console.log('获取通知文书数据，ID:', props.id);
    
    // 构建带分页参数的请求数据
    const data = {
      pageNo: pagination.current,
      pageSize: pagination.pageSize,
      applicationId: props.id
    };
    
    const res = await getDocumentListByApplicationId(data);
    if (res.code === 200) {
      documentList.value = res.data.records || res.data || [];
      pagination.total = res.data.total || 0;
    }
  } catch (error) {
    console.error('获取通知文书数据失败:', error);
    message.error('获取数据失败，请稍后重试');
  }
};

const getFileName = (url) => {
  if (!url || typeof url !== 'string') {
    return '';
  }

  let arr = url.split("/");
  let file = arr[arr.length - 1];

  // 如果文件名包含点号且不以点号开头，则截取掉后缀
  const dotIndex = file.lastIndexOf(".");
  if (dotIndex > 0) {
    return file.substring(0, dotIndex);
  }

  return file;
};

// 组件挂载时获取数据
onMounted(async () => {
  await fetchDocumentData();
});

// 处理签收成功事件
const handleReceiveSuccess = () => {
  // 更新文档列表数据
  fetchDocumentData();
  // 通知父组件更新未读通知数量
  emit('update-unread-count');
}
</script>

<style scoped>
.tab-content {
  background-color: #fff;
  margin-bottom: 80px;
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  padding: 5px 20px;
  border-bottom: 1px solid #e8e8e8;
}

.table-container {
  padding: 0 20px;
  margin-top: 10px;
}

.action-btn {
  padding-left: 5px;
  padding-right: 5px;
}

.empty-state {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 300px;
  color: #999;
  font-size: 14px;
}

.text-xs {
  font-size: 12px;
}

.text-gray-500 {
  color: #8c8c8c;
}

.mt-1 {
  margin-top: 4px;
}

/* 表格样式优化 */
:deep(.ant-table) {
  border: 1px solid #f0f0f0;
  border-radius: 4px;
  overflow: hidden;
}

:deep(.ant-table-thead > tr > th) {
  background-color: #fafafa;
  font-weight: 500;
  text-align: center;
}

:deep(.ant-table-tbody > tr > td) {
  text-align: center;
}

:deep(.ant-table-tbody .ant-btn-link) {
  margin: 0 1px !important;
}

/* 布局工具类 */
.flex {
  display: flex;
}

.jcsb {
  justify-content: space-between;
}

.ml24 {
  margin-left: 24px;
}

.mt22 {
  margin-top: 22px;
}

.mb18 {
  margin-bottom: 18px;
}
</style>